<template>
  <div class="chart-container">
    <b-card>
      <b-card-header>
        <b-card-title>
          Quantity and Value of Products Sold per Client Over Time
        </b-card-title>
      </b-card-header>

      <b-row class="mb-3">
        <b-col>
          <label for="clientFilter">Select Clients (max 5):</label>
          <b-form-select
            id="clientFilter"
            v-model="selectedClients"
            :options="clientOptions"
            multiple
            :select-size="4"
            @change="updateChart"
          ></b-form-select>
        </b-col>
      </b-row>

      <apexcharts
        type="line"
        height="500"
        :options="chartOptions"
        :series="chartSeries"
      ></apexcharts>
    </b-card>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import {
  BCard,
  BCardTitle,
  BCardHeader,
  BRow,
  BCol,
  BFormInput,
  BFormSelect,
} from "bootstrap-vue";

export default {
  name: "ProductsSoldPerClientChart",
  components: {
    apexcharts: VueApexCharts,
    BCard,
    BCardTitle,
    BCardHeader,
    BRow,
    BCol,
    BFormInput,
    BFormSelect,
  },
  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      startDate: "",
      endDate: "",
      selectedClients: [],
    };
  },
  computed: {
    clientOptions() {
      return this.uniqueClients.map(client => ({
        value: client,
        text: client
      }));
    },

    sortedDates() {
      const dates = [...new Set(this.data.map((item) => item.period))];
      return dates.sort();
    },
    
    uniqueClients() {
      return [...new Set(this.data.map((item) => item.clientName))];
    },

    filteredData() {
      let filtered = this.data;
      
      if (this.startDate && this.endDate) {
        filtered = filtered.filter((item) => {
          const itemDate = new Date(item.period);
          return itemDate >= new Date(this.startDate) && itemDate <= new Date(this.endDate);
        });
      }

      if (this.selectedClients.length > 0) {
        filtered = filtered.filter(item => this.selectedClients.includes(item.clientName));
      }

      return filtered;
    },
  
    chartSeries() {
      const series = [];
      const clientsToShow = this.selectedClients.length > 0 
        ? this.selectedClients 
        : this.uniqueClients.slice(0, 5); 

      clientsToShow.forEach((clientName) => {

        const quantityData = this.sortedDates.map((period) => {
          const periodData = this.filteredData.find(
            (item) => item.clientName === clientName && item.period === period
          );
          return periodData ? periodData.totalQuantitySold : null;
        });
        series.push({
          name: `${clientName} - Quantity`,
          type: "line",
          data: quantityData,
        });

        const valueData = this.sortedDates.map((period) => {
          const periodData = this.filteredData.find(
            (item) => item.clientName === clientName && item.period === period
          );
          return periodData ? periodData.totalValueSold : null;
        });
        series.push({
          name: `${clientName} - Value`,
          type: "line",
          data: valueData,
        });
      });
      return series;
    },

    chartOptions() {
      return {
        chart: {
          id: "products-sold-per-client",
          type: "line",
          stacked: false,
          animations: {
            enabled: false
          },
          zoom: {
            enabled: true
          }
        },
        xaxis: {
          categories: this.sortedDates,
          title: { text: "Period" },
          labels: {
            rotate: -45,
            trim: true
          }
        },
        yaxis: [
          {
            title: { 
              text: "Quantity",
              style: {
                fontSize: '12px',
              }
            },
            decimalsInFloat: 2,
          },
          {
            opposite: true,
            title: { 
              text: "Value (Rs)",
              style: {
                fontSize: '12px',
              }
            },
            decimalsInFloat: 2,
          }
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function(value, { seriesIndex }) {
              if (value === null) return "No data";
              return seriesIndex % 2 === 0 
                ? value.toFixed(2) + " units"
                : "Rs" + value.toFixed(2);
            }
          }
        },
        legend: {
          position: "bottom",
          horizontalAlign: "center",
          offsetY: 8,
          fontSize: '12px',
          itemMargin: {
            horizontal: 10,
            vertical: 5
          }
        },
        stroke: {
          width: 2,
          curve: 'smooth'
        },
        colors: ["#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0", "#546E7A", "#26a69a", "#D10CE8"],
        markers: {
          size: 5,
        },
        grid: {
          xaxis: {
            lines: {
              show: true
            }
          }
        }
      };
    },
  },
  methods: {
    updateChart() {
    },
  },
  mounted() {
    if (!this.startDate || !this.endDate) {
      const dates = this.sortedDates;
      this.startDate = dates[0];
      this.endDate = dates[dates.length - 1];
    }

    if (this.uniqueClients.length > 0) {
      this.selectedClients = this.uniqueClients.slice(0, 5);
    }
  },
};
</script>

<style scoped>
.chart-container {
  padding: 1rem;
}
</style>